import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Fingerprint } from 'lucide-react'
import { useAuth } from '@/app/auth-context'
import { Button } from '@/components/ui'

/**
 * Mock SSO entry point. A real deployment redirects to the org's IdP; in
 * mock mode we simulate the round-trip with a short delay and then sign in
 * through `signInWithSso`, which synthesizes a session the same way the
 * credential `signIn` path does (fixed executive identity). SSO deliberately
 * ignores the form lockout — it is a separate trust path.
 *
 * In oidc mode `signInWithSso` redirects to the BFF login, which hands off to
 * Keycloak — its hosted page renders whatever identity providers the realm
 * brokers, so there is no separate SSO endpoint to wire.
 */

const SSO_IDENTITY = 'ciso@acmecorp.com'
const REDIRECT_SIMULATION_MS = 650

export function SsoButton({ disabled }: { disabled?: boolean }) {
  const { signInWithSso } = useAuth()
  const navigate = useNavigate()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function onClick() {
    if (pending) return
    setError(null)
    setPending(true)
    new Promise((resolve) => setTimeout(resolve, REDIRECT_SIMULATION_MS))
      .then(() => signInWithSso(SSO_IDENTITY))
      .then(() => navigate({ to: '/orgs' }))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'SSO sign-in failed')
        setPending(false)
      })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
      <Button
        variant="outline"
        size="lg"
        leadingIcon={Fingerprint}
        loading={pending}
        disabled={disabled}
        onClick={onClick}
        style={{ width: '100%' }}
      >
        {pending ? 'Contacting identity provider…' : 'Continue with SSO'}
      </Button>
      {error && (
        <div
          role="alert"
          style={{ fontSize: 'var(--fs-12)', color: 'var(--err-700)' }}
        >
          {error}
        </div>
      )}
    </div>
  )
}
